import React, { Component } from "react"
import Input from "@kit/design/input"
import Stack from "@kit/design/stack"
import { Field, CheckboxField } from "core/form"
import { TagSelect } from "app/shared/selects"
import { createTrigger, updateTrigger, getTriggers } from "./utils"
import { Dropdown } from "core/dropdown"
import "./toolbar-menu.css"
import { find, get } from "lodash"
import { OldButton } from "@kit/design/old-button"

const createOrUpdateTrigger = async (triggers, id, { href, tag }) => {
  const trigger = id && triggers.find(trigger => trigger.id == id)

  const result = trigger
    ? await updateTrigger(trigger, { url: href, tag })
    : await createTrigger({ url: href, tag })

  return {
    id: id || result.rule.triggers[0].id
  }
}

const TriggerDropdownItem = props => {
  const { trigger, onSelect } = props
  return (
    <Dropdown.Item
      onClick={() => onSelect(trigger)}
      style={{ maxWidth: "300px" }}
    >
      <span className="font-semibold">
        {get(trigger, "configuration.name", "Trigger")}
      </span>
      &nbsp;
      <span>{trigger.initiator_value}</span>
    </Dropdown.Item>
  )
}

export class LinkTriggerForm extends Component {
  constructor(props) {
    super(props)
    const { href, tag, triggerId, triggerName, existingTrigger } = props.node
      ? props.node.data.toJS()
      : {}
    const displayName = name && name != href

    this.state = {
      href,
      tag,
      triggerId,
      triggerName,
      tagSubscribers: !!tag,
      isAuthorized: false,
      triggers: {},
      prevNodeKey: props.node.key,
      existingTrigger,
      displayName
    }
  }

  static getDerivedStateFromProps(props, state) {
    const { href, tag, triggerId } = props.node ? props.node.data.toJS() : {}

    if (props.node.key !== state.prevNodeKey) {
      return {
        href,
        tag,
        triggerId,
        tagSubscribers: !!tag,
        triggers: {}
      }
    }

    return null
  }

  async componentDidMount() {
    const { triggers, isAuthorized } = await getTriggers()
    this.setState({ triggers, isAuthorized })
  }

  handleChange = event => {
    const { editor, node } = this.props
    const href = event.target.value.trim()

    editor.command("setNodeByKey", node.key, {
      data: { ...node.data.toJS(), href }
    })

    this.setState({ href })
  }

  handleTagSubscribersChange = event => {
    const checked = event.target.checked
    this.setState({ tagSubscribers: checked, tag: undefined })
  }

  handleTagChange = async event => {
    const { node, editor } = this.props
    const value = event.target.value
    const { value: id, label: name } = value
    const tag = { id, name }

    editor.command("setNodeByKey", node.key, {
      data: { ...node.data.toJS(), tag }
    })

    const href = node.data.get("href")
    const triggerId = node.data.get("triggerId")
    const triggers = this.state.triggers

    const trigger = await createOrUpdateTrigger(triggers, triggerId, {
      href,
      tag
    })

    editor.command("setNodeByKey", node.key, {
      data: { ...node.data.toJS(), href, tag, triggerId: trigger.id }
    })

    this.setState({ href })
  }

  handleTriggerSelect = trigger => {
    const { node, editor } = this.props
    const tag = find(trigger.actions, { kind: "Action::TagAdd" })
    const href = trigger.initiator_value
    const triggerName = get(trigger, "configuration.name")

    this.setState({
      tag,
      tagSubscribers: !!tag,
      href,
      existingTrigger: true,
      triggerId: trigger.id,
      triggerName
    })

    editor.command("setNodeByKey", node.key, {
      data: {
        ...node.data.toJS(),
        href,
        tag,
        triggerId: trigger.id,
        existingTrigger: true
      }
    })
  }

  handleTriggerClear = () => {
    const { node, editor } = this.props
    let blankAttributes = {
      tag: null,
      href: "",
      existingTrigger: false,
      triggerId: null,
      triggerName: null,
      tagSubscribers: false
    }

    this.setState(blankAttributes)
    editor.command("setNodeByKey", node.key, {
      data: { ...node.data.toJS(), ...blankAttributes }
    })
  }

  render() {
    const { editor } = this.props
    const { target } = editor.props

    const {
      href,
      tagSubscribers,
      tag,
      existingTrigger,
      isAuthorized,
      triggers,
      triggerId,
      triggerName
    } = this.state

    const trigger =
      triggerId &&
      triggers.length > 0 &&
      triggers.find(trigger => trigger.id == triggerId)

    // Use the trigger name from an existing link trigger or fetch it from the trigger's options.
    const name = triggerName || (trigger && get(trigger, "configuration.name"))

    // If the "Tag subscribers with ..." is selected, we'll have a tag here,
    // otherwise find a TagAdd action from the link trigger rule and use that.
    const linkTag =
      tag || (trigger && find(trigger.actions, { kind: "Action::TagAdd" }))

    // This is a bool for the tag checkbox, it should be on if either a link trigger or
    // the "Tag subscribers with ..." option is selected.
    const linkTagSubscribers = tagSubscribers || !!linkTag

    // Don't display name for link triggers created in the editor (where URL and name matches).
    const displayName = name && name != href

    // Instead of the URL label, we use the name of the link trigger if it exists.
    const urlLabel = displayName ? name : "URL"

    // Creators can't use link triggers on landing pages
    const canUseLinkTriggers =
      target === "email" && !existingTrigger && isAuthorized

    return (
      <Stack spacing={2}>
        <div className="flex items-end">
          <Field
            label={urlLabel}
            name="href"
            onChange={this.handleChange}
            value={href}
            control={Input}
            disabled={existingTrigger}
            className="mr-2"
          />
          {triggers.length > 0 && (
            <Dropdown
              title="Link triggers"
              position="right"
              icon="down"
              buttonClassNames="ck-button--dark"
            >
              {triggers.map(trigger => (
                <TriggerDropdownItem
                  key={trigger.id}
                  trigger={trigger}
                  onSelect={this.handleTriggerSelect}
                />
              ))}
            </Dropdown>
          )}
        </div>
        {canUseLinkTriggers && (
          <div className="mt-2">
            <CheckboxField
              label="Tag subscribers who click this link"
              onChange={this.handleTagSubscribersChange}
              name="tagSubscribers"
              checked={linkTagSubscribers}
            />
          </div>
        )}
        {existingTrigger && trigger && (
          <div className="flex mt-2">
            <OldButton
              className="grow-0"
              target="_blank"
              href={`/rules/${trigger.rule_id}/edit`}
            >
              Show link trigger
            </OldButton>

            <OldButton
              type="button"
              onClick={this.handleTriggerClear}
              icon="close"
              className="ml-auto mr-px block py-1 px-2 rounded-sm font-semibold text-xs text-gray-800 bg-gray-100 hover:bg-gray-300 focus:outline-none"
            >
              Clear trigger
            </OldButton>
          </div>
        )}
        {linkTagSubscribers && !existingTrigger && (
          <Field
            label="Tag"
            name="tag"
            onChange={this.handleTagChange}
            value={linkTag && linkTag.id}
            isMulti={false}
            control={TagSelect}
          />
        )}
      </Stack>
    )
  }
}
